<script>
import bem from '@/utils/bem';
import { mapActions, mapState } from 'vuex';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditDescriptionModal from '@/components/pageContent/modules/EditDescriptionModal.vue';
import desktopLogo from '@/assets/images/pageContents/vintage-university-desktop.png';
import ImagesWithStripes from './images-with-stripes.vue';

const b = bem('blogs-block');

export default {
  components: {
    PencilIcon,
    EditDescriptionModal,
    ImagesWithStripes,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: true,
    },
    pageKey: {
      type: String,
      default: '',
    },
  },
  emits: ['update'],
  data() {
    return {
      logoSrc: desktopLogo,
      isDescriptionModalVisible: false,
      isArticle1ModalVisible: false,
      isArticle2ModalVisible: false,
      isArticle3ModalVisible: false,
    };
  },
  computed: {
    ...mapState({
      articlesList: (state) => state.blogs.articlesList,
      isArticlesLoaded: (state) => state.blogs.isArticlesLoaded,
    }),
    hasDescription() {
      return !!(this.data.description || this.data.descriptionMobile);
    },
    displayDescription() {
      if (this.isMobileView && this.data.descriptionMobile) {
        return this.data.descriptionMobile;
      }
      return this.data.description || '';
    },
    selectedArticles() {
      const articles = [];
      const ids = [this.data.article1Id, this.data.article2Id, this.data.article3Id];
      ids.forEach((id) => {
        if (id && this.articlesList) {
          const found = this.articlesList.find((a) => a.id === id);
          if (found) {
            articles.push(found);
          } else {
            articles.push(null);
          }
        } else {
          articles.push(null);
        }
      });
      return articles;
    },
    // Convert flat data to stripes format for ImagesWithStripes
    leftStripes() {
      return [
        {
          id: 1,
          text: '',
          color: '#ECC486',
          colorText: '#293241',
        },
        {
          id: 2,
          text: this.data.leftButtonText || 'Learn More @ Vintage University',
          textMob: this.data.leftButtonTextMobile || this.data.leftButtonText || 'Learn More @ Vintage University',
          color: '#293241',
          colorText: '#fff',
          link: this.data.leftButtonLink || '',
        },
      ];
    },
    rightStripes() {
      return [
        {
          id: 1,
          text: '',
          color: '#ECC486',
          colorText: '#293241',
        },
        {
          id: 2,
          text: this.data.rightButtonText || 'Take Our Guided Tour of FS HQ',
          textMob: this.data.rightButtonTextMobile || this.data.rightButtonText || 'Take Our Guided Tour of FS HQ',
          color: '#293241',
          colorText: '#fff',
          link: this.data.rightButtonLink || '',
        },
      ];
    },
    withBottomSection() {
      return this.data.withBottomSection || false;
    },
    isHomePage() {
      return this.pageKey === 'home';
    },
  },
  mounted() {
    if (!this.isArticlesLoaded) {
      this.getArticlesAction({ limit: 100, offset: 0 });
    }
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getArticlesAction: 'blogs/getArticles',
    }),
    toggleBottomSection() {
      this.$emit('update', {
        ...this.data,
        withBottomSection: !this.withBottomSection,
      });
    },
    openDescriptionModal() {
      if (this.isEditMode) {
        this.isDescriptionModalVisible = true;
      }
    },
    openArticleModal(index) {
      if (!this.isEditMode) return;
      if (index === 0) this.isArticle1ModalVisible = true;
      if (index === 1) this.isArticle2ModalVisible = true;
      if (index === 2) this.isArticle3ModalVisible = true;
    },
    handleDescriptionSave(values) {
      this.$emit('update', {
        ...this.data,
        description: values.description,
        descriptionMobile: values.descriptionMobile,
      });
      this.isDescriptionModalVisible = false;
    },
    handleDescriptionDelete() {
      this.$emit('update', {
        ...this.data,
        description: '',
        descriptionMobile: '',
      });
      this.isDescriptionModalVisible = false;
    },
    handleArticleSelect(index, articleId) {
      const key = `article${index + 1}Id`;
      this.$emit('update', {
        ...this.data,
        [key]: articleId,
      });
      if (index === 0) this.isArticle1ModalVisible = false;
      if (index === 1) this.isArticle2ModalVisible = false;
      if (index === 2) this.isArticle3ModalVisible = false;
    },
    // Handle updates from ImagesWithStripes component
    handleImagesUpdate(updates) {
      const newData = { ...this.data };

      // Handle image updates
      if ('leftImage' in updates) newData.leftImage = updates.leftImage;
      if ('leftImageMobile' in updates) newData.leftImageMobile = updates.leftImageMobile;
      if ('rightImage' in updates) newData.rightImage = updates.rightImage;
      if ('rightImageMobile' in updates) newData.rightImageMobile = updates.rightImageMobile;

      // Handle stripe updates - convert back to flat format
      if (updates.leftStripes) {
        const stripe = updates.leftStripes[1]; // Second stripe has text/link
        if (stripe) {
          newData.leftButtonText = stripe.text;
          newData.leftButtonTextMobile = stripe.textMob;
          newData.leftButtonLink = stripe.link;
        }
      }
      if (updates.rightStripes) {
        const stripe = updates.rightStripes[1]; // Second stripe has text/link
        if (stripe) {
          newData.rightButtonText = stripe.text;
          newData.rightButtonTextMobile = stripe.textMob;
          newData.rightButtonLink = stripe.link;
        }
      }

      this.$emit('update', newData);
    },
    getArticleImage(article) {
      return article?.previewImageUri || article?.imageUrl || '';
    },
    getArticleTitle(article) {
      return article?.cardTitle || article?.title || '';
    },
  },
};
</script>

<template>
  <article :class="b({ isMobileView, isEditMode, isHomePage })">
    <div :class="b('logo-wrap', { isMobileView })">
      <div :class="b('left-side', { isMobileView })">
        <div :class="b('line', { reverse: true, isWhite: isHomePage })" />
      </div>
      <img
        :class="b('logo', { isMobileView, isHomePage })"
        :src="logoSrc"
        alt="Vintage University"
      />
      <div :class="b('right-side', { isMobileView })">
        <div
          :class="b('line', {
            horizon: !isHomePage,
            jagged: isHomePage,
            small: isMobileView,
          })"
        />
        <div :class="b('line', { small: true, isMobileView, isWhite: isHomePage })" />
      </div>
    </div>

    <!-- Description section -->
    <div
      v-if="hasDescription"
      :class="b('description-wrap', { editable: isEditMode, isMobileView })"
      role="button"
      tabindex="0"
      @click="openDescriptionModal"
      @keydown.enter="openDescriptionModal"
    >
      <p :class="b('description', { isWhite: isHomePage })">{{ displayDescription }}</p>
      <PencilIcon v-if="isEditMode" :size="20" color="#293241" :class="b('edit-icon')" />
    </div>
    <button
      v-else-if="isEditMode"
      :class="b('add-btn')"
      type="button"
      @click="openDescriptionModal"
    >
      + Add Description
    </button>

    <!-- Article cards -->
    <div :class="b('card-wrapper', { isMobileView })">
      <div
        v-for="(article, index) in selectedArticles"
        :key="index"
        :class="b('card', { editable: isEditMode, isMobileView })"
        role="button"
        tabindex="0"
        @click="openArticleModal(index)"
        @keydown.enter="openArticleModal(index)"
      >
        <div
          :class="b('card-image', { isMobileView })"
          :style="article && getArticleImage(article) ? {
            backgroundImage: `url(${getArticleImage(article)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          } : {}"
        >
          <span v-if="!article" :class="b('card-placeholder')">
            <span :class="b('card-placeholder-plus')">+</span><br>
            Select Article {{ index + 1 }}
          </span>
        </div>
        <h3 :class="b('card-title', { isMobileView, isHomePage })">
          {{ article ? getArticleTitle(article) : 'Article Title' }}
        </h3>
        <span :class="b('card-link', { isMobileView, isHomePage })">Read more</span>
      </div>
    </div>

    <!-- Toggle for bottom section (edit mode only) -->
    <div v-if="isEditMode" :class="b('toggle-controls')">
      <div :class="b('toggle-item')">
        <span :class="b('toggle-label')">Bottom Section:</span>
        <a-switch
          :checked="withBottomSection"
          @change="toggleBottomSection"
        />
      </div>
    </div>

    <!-- Bottom section with images and buttons -->
    <ImagesWithStripes
      v-if="withBottomSection"
      :class="b('images-list', { isMobileView })"
      :left-image="data.leftImage"
      :left-image-mobile="data.leftImageMobile"
      :right-image="data.rightImage"
      :right-image-mobile="data.rightImageMobile"
      :left-stripes="leftStripes"
      :right-stripes="rightStripes"
      :is-blogs-block="true"
      :is-mobile-view="isMobileView"
      :is-edit-mode="isEditMode"
      @update="handleImagesUpdate"
    />

    <!-- Modals -->
    <EditDescriptionModal
      :visible="isDescriptionModalVisible"
      :description="data.description"
      :description-mobile="data.descriptionMobile"
      @close="isDescriptionModalVisible = false"
      @save="handleDescriptionSave"
      @delete="handleDescriptionDelete"
    />

    <!-- Article selection modals -->
    <a-modal
      :open="isArticle1ModalVisible"
      title="Select Article 1"
      :footer="null"
      width="800px"
      @cancel="isArticle1ModalVisible = false"
    >
      <div :class="b('article-list')">
        <div
          v-for="article in articlesList"
          :key="article.id"
          :class="b('article-option', { selected: data.article1Id === article.id })"
          @click="handleArticleSelect(0, article.id)"
          @keyup.enter="handleArticleSelect(0, article.id)"
        >
          <img
            v-if="getArticleImage(article)"
            :src="getArticleImage(article)"
            :class="b('article-option-image')"
            alt=""
          />
          <span :class="b('article-option-title')">{{ getArticleTitle(article) }}</span>
        </div>
      </div>
    </a-modal>

    <a-modal
      :open="isArticle2ModalVisible"
      title="Select Article 2"
      :footer="null"
      width="800px"
      @cancel="isArticle2ModalVisible = false"
    >
      <div :class="b('article-list')">
        <div
          v-for="article in articlesList"
          :key="article.id"
          :class="b('article-option', { selected: data.article2Id === article.id })"
          @click="handleArticleSelect(1, article.id)"
          @keyup.enter="handleArticleSelect(1, article.id)"
        >
          <img
            v-if="getArticleImage(article)"
            :src="getArticleImage(article)"
            :class="b('article-option-image')"
            alt=""
          />
          <span :class="b('article-option-title')">{{ getArticleTitle(article) }}</span>
        </div>
      </div>
    </a-modal>

    <a-modal
      :open="isArticle3ModalVisible"
      title="Select Article 3"
      :footer="null"
      width="800px"
      @cancel="isArticle3ModalVisible = false"
    >
      <div :class="b('article-list')">
        <div
          v-for="article in articlesList"
          :key="article.id"
          :class="b('article-option', { selected: data.article3Id === article.id })"
          @click="handleArticleSelect(2, article.id)"
          @keyup.enter="handleArticleSelect(2, article.id)"
        >
          <img
            v-if="getArticleImage(article)"
            :src="getArticleImage(article)"
            :class="b('article-option-image')"
            alt=""
          />
          <span :class="b('article-option-title')">{{ getArticleTitle(article) }}</span>
        </div>
      </div>
    </a-modal>

  </article>
</template>

<style scoped lang="scss">
@import '@/components/newDesign/blogs-block-style.scss';
</style>
